import React, { useState, useEffect } from "react";
import { backendUrl } from "../backendUrl";
import { Link } from "react-router-dom";
import { Configuration, SystemInfo, ConfigurationDetail } from "./types";
import { findPrice } from "./helpers";
import DynamicIcons from "./DynamicIcons";
import LockSystemDetails from "./LockSystemDetails";

interface LockSystemRowProps {
  product: Configuration;
  index: number;
  expanded: boolean;
  onToggle: (index: number) => void;
  onSelect: (supllier: string, system1: string) => void;
  configuration: ConfigurationDetail;
  tableData: SystemInfo[];
  supplierIconMap: Record<string, string>;
}

const LockSystemRow: React.FC<LockSystemRowProps> = ({
  product,
  index,
  expanded,
  onToggle,
  onSelect,
  configuration,
  tableData,
  supplierIconMap,
}) => {
  // Detekcja szerokości ekranu i orientacji
  const [isMobile, setIsMobile] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width < 960);
      setIsLandscape(width > height);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const price = (() => {
    const priceValue = findPrice(configuration, product.supllier, product.system1);
    if (typeof priceValue === "number" && !isNaN(priceValue)) {
      return `${priceValue.toFixed(2).replace(".", ",")} €`;
    } else {
      return "Brak";
    }
  })();

  // Get supplier icon path from database or fallback to hardcoded path
  const getSupplierIcon = () => {
    const iconPath = supplierIconMap[product.supllier];
    if (iconPath) {
      return `${backendUrl}${iconPath}`;
    }
    // Fallback to old hardcoded path if not in database
    return `${backendUrl}/img/${product.supllier.trim()}.jpeg`;
  };

  // Mobile LANDSCAPE - siatka kolumn jak na stronie 3
  if (isMobile && isLandscape) {
    return (
      <>
        <div key={index} className="landscape-row">
          {/* Kolumna 1: Logo + Nazwa */}
          <div className="landscape-col landscape-col-product">
            <img
              className="landscape-logo"
              src={getSupplierIcon()}
              alt="logo"
              loading="lazy"
              decoding="async"
              crossOrigin="anonymous"
            />
            <div className="landscape-product-info">
              <span className="landscape-supplier">{product.supllier}</span>
              <span className="landscape-system">{product.system1}</span>
            </div>
          </div>

          {/* Kolumna 2: Ikony właściwości */}
          <div className="landscape-col landscape-col-properties">
            <DynamicIcons product={product} />
          </div>

          {/* Kolumna 3: Cena */}
          <div className="landscape-col landscape-col-price">
            {price}
          </div>

          {/* Kolumna 4: Przyciski */}
          <div className="landscape-col landscape-col-actions">
            <button 
              className="landscape-expand-btn" 
              onClick={() => onToggle(index)}
              title="Details anzeigen"
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <path d="M6 9l6 6 6-6" stroke="#005CA9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <Link to={"/3"}>
              <button
                onClick={() => onSelect(product.supllier, product.system1)}
                className="landscape-select-btn"
              >
                Auswählen
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '4px' }}>
                  <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </Link>
          </div>
        </div>

        {/* Rozwinięte szczegóły */}
        {expanded && (
          <div className="landscape-details">
            <LockSystemDetails product={product} tableData={tableData} />
          </div>
        )}
      </>
    );
  }

  // Mobilny układ karty PORTRAIT
  if (isMobile) {
    return (
      <>
        <div key={index} className="mobile-card">
          {/* Nagłówek karty z logo, nazwą systemu i ceną */}
          <div className="mobile-card-header">
            <img
              className="mobile-card-logo"
              src={getSupplierIcon()}
              alt="logo"
              loading="lazy"
              decoding="async"
              crossOrigin="anonymous"
            />
            <div className="mobile-card-title">
              <div className="mobile-card-supplier">{product.supllier}</div>
              <div className="mobile-card-system">{product.system1}</div>
            </div>
            <div className="mobile-card-price">{price}</div>
          </div>

          {/* Ikony właściwości */}
          <div className="mobile-card-properties">
            <DynamicIcons product={product} />
          </div>

          {/* Stopka karty z przyciskami */}
          <div className="mobile-card-footer">
            <Link to={"/3"} style={{ flex: 1 }}>
              <button
                onClick={() => onSelect(product.supllier, product.system1)}
                className="mobile-card-select-btn"
              >
                Auswählen und fortfahren
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M9 18l6-6-6-6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </Link>
            
            <button 
              className="mobile-card-expand-btn" 
              onClick={() => onToggle(index)}
              title="Details anzeigen"
            >
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none"
                style={{ 
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease'
                }}
              >
                <path d="M6 9l6 6 6-6" stroke="#005CA9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          {/* Rozwinięte szczegóły */}
          {expanded && (
            <div className="mobile-card-details">
              <LockSystemDetails product={product} tableData={tableData} />
            </div>
          )}
        </div>
      </>
    );
  }

  // Desktop układ (oryginalny)
  return (
  <div
    key={index}
    style={{
      display: "flex",
      flexDirection: "column",
      border: "1px solid #B1B1C5",
      backgroundColor: "#F3F3F3",
      margin: "0.5% 1% 0.5% 1%",
      justifyContent: "center",
      padding: "5px 0px",
    }}
  >
    <div style={{ display: "flex", flexDirection: "row" }}>
      <div style={{ flexBasis: "25%", flexGrow: 0, display: "flex", flexFlow: "wrap", alignItems: "center" }}>
        <img
          style={{ width: "70px", height: "40px", float: "left", margin: "0px 10px" }}
          src={getSupplierIcon()}
          alt="logo"
          loading="lazy"
          decoding="async"
          crossOrigin="anonymous"
        />
        <div>
          <span style={{ fontSize: "14px", fontWeight: "bold", color: "black", justifyContent: "center" }}>
            {product.supllier}
          </span>
          <br />
          <span style={{ fontSize: "14px", color: "black" }}>{product.system1}</span>
        </div>
      </div>
      <div style={{ flexBasis: "28%", flexGrow: 0, fontSize: "14px", alignItems: "center", display: "flex", flexFlow: "wrap", justifyContent: "center" }}>
        <DynamicIcons product={product} />
      </div>
      <div style={{ flexBasis: "10%", flexGrow: 0, fontSize: "14px", color: "black", fontWeight: "bold", alignContent: "center", display: "flex", flexFlow: "wrap", justifyContent: "center", paddingLeft: "3%", marginLeft: "8%" }}>
        {price}
      </div>
      <div style={{ flexBasis: "5%", flexGrow: 0, textAlign: "center", alignContent: "center", display: "flex", flexFlow: "wrap" }} title="Weitere Informationen">
        {expanded ? (
          <img
            style={{ width: "25px", height: "25px", rotate: "180deg", cursor: "pointer" }}
            onClick={() => onToggle(index)}
            src={`${backendUrl}/img/DeltaButton.svg`}
            alt="arrow"
            loading="lazy"
            decoding="async"
            crossOrigin="anonymous"
          />
        ) : (
          <img
            style={{ width: "25px", height: "25px", cursor: "pointer" }}
            onClick={() => onToggle(index)}
            src={`${backendUrl}/img/DeltaButton.svg`}
            alt="arrow"
            loading="lazy"
            decoding="async"
            crossOrigin="anonymous"
          />
        )}
      </div>
      <div style={{ flexBasis: "25%", flexGrow: 0, textAlign: "center", display: "flex", flexFlow: "wrap", alignContent: "center", justifyContent: "center" }}>
        <Link to={"/3"}>
          <button
            onClick={() => onSelect(product.supllier, product.system1)}
            style={{ backgroundColor: "#005CA9", color: "white", border: "none", padding: "5px 10px", cursor: "pointer", alignItems: "center", display: "flex" }}
          >
            <div style={{ color: "white", border: "none", padding: "5px 10px", alignItems: "center" }}>
              Auswählen und fortfahren
            </div>
            <img className="vector" alt="Vector" src={`${backendUrl}/img/Vector.svg`} loading="lazy" decoding="async" crossOrigin="anonymous" />
          </button>
        </Link>
      </div>
    </div>
    {expanded && <LockSystemDetails product={product} tableData={tableData} />}
  </div>
  );
};

export default LockSystemRow;
